import swal from 'sweetalert2'
import { getUnix } from '@/libs/date-format'
import router from '@/router'
import store from '@/store'
import i18n from './i18n'

/**
 * Wrapper for the success modal
 *
 * @param {Object} options                    Configurations for the alert
 * @param {string} options.title              Title that should be printed in the text alert
 * @param {Object} options.router             Router object
 * @param {string} [options.router.redirect]  Redirect to the route
 * @param {function} [options.fetch]          Function to fetch the data
 */
export const successAlert = options => {
  swal.fire({
    icon: 'success',
    title: i18n.t(options.alertTitle),
    text: i18n.t('The {title} has been {action}', { title: i18n.t(options.title), action: i18n.t(options.action) }),
    customClass: {
      confirmButton: 'btn btn-success',
    },
  }).then(() => {
    if (options?.router?.redirect) {
      const { redirect, params } = options.router

      const routeOptions = {
        name: redirect,
        ...(params && Object.keys(params).length > 0 ? { params } : {})
      }

      router.push(routeOptions)
    }

    if (options?.fetch) {
      options.fetch()
    }
  })
}

/**
 * Wrapper for the error modal
 *
 * @param {Object} options        Configurations for the alert
 * @param {string} options.title  Title that should be printed in the text alert
 */
export const errorAlert = options => {
  swal.fire('Error', i18n.t('Something went wrong deleting the {title}', { title: i18n.t(options.title) }), 'error')
}

/**
 * Wrapper for the confirm modal
 *
 * @param {Object} current  The current value of the form
 *
 * @param {Object} options                    Configurations for the alert
 * @param {string} options.title              Title that should be printed in the text alert
 * @param {string} options.text               Text of the alert
 * @param {Object} options.router             Router object with settings
 * @param {string} options.router.path        Path to the route to submit the values
 * @param {string} [options.router.redirect]  Name of the route to redirect to
 * @param {function} [options.fetch]          Function to fetch data
 */
export const confirmDelete = (current, options) => {
  if (!options.title) options.title = 'item'

  swal.fire({
    title: i18n.t('Are you ABSOLUTELY sure?'),
    text: i18n.t('You are about to permanently delete this {title}. You won\'t be able to revert this.', { title: i18n.t(options.title) }),
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: i18n.t('Yes, delete it!'),
    customClass: {
      confirmButton: 'btn btn-primary',
      cancelButton: 'btn btn-outline-danger ml-1',
    },
    buttonsStyling: false,
  }).then(res => {
    // Define the current as ref style if it's not defined as reference
    const reference = current?.value ?? current

    if (res.isConfirmed) {
      reference.deletedAt = getUnix()

      store.dispatch(options.router.path, reference)
        .then(() => {
          successAlert({
            title: options.title,
            action: 'deleted',
            alertTitle: 'Deleted!',
            router: options.router,
            fetch: options.fetch,
          })
        })
    }
  }).catch(e => {
    errorAlert({ title: options.title })
  })
}

/**
 * Wrapper for the confirm modal
 *
 * @param {Object} options                    Configurations for the alert
 * @param {string} options.title              Title of the alert
 * @param {string} options.text               Text of the alert
 * @param {string} options.icon               Icon of the alert
 * @param {string} options.confirmButtonText  Confirm Button text of the alert
 * @param {Boolean} options.cancelButton      Boolean for showing cancelButton
 * @param {string} options.cancelButtonText   Cancel Button text of the alert
 */
export const confirmModal = (options) => {
  return swal.fire({
    title: options.title,
    text: options.text,
    html: options.html,
    icon: options.icon,
    showCancelButton: options.cancelButton ?? true,
    confirmButtonText: options.confirmButtonText,
    cancelButtonText: options.cancelButtonText || i18n.t('Cancel'),
    customClass: {
      confirmButton: options.customClass?.confirmButton ?? 'btn btn-primary',
      cancelButton: options.customClass?.cancelButton ?? 'btn btn-outline-danger ml-1',
    },
    buttonsStyling: false,
  }).then(res => {
    return res.isConfirmed
  }).catch(() => {
    errorAlert({ title: options.title })
  })
}

/**
 * Confirm modal with typed input verification.
 *
 * SweetAlert2 assigns `inputLabel` with `innerText`, so HTML is not rendered.
 * Pass `inputLabelHtml` when the label needs markup (e.g. a badge); the label
 * node is created then filled via `didOpen`.
 *
 * @param {Object} options
 * @param {string} options.title
 * @param {string} [options.text]
 * @param {string} [options.html]
 * @param {string} [options.icon]
 * @param {string} [options.confirmValue] Single accepted value (legacy).
 * @param {string[]} [options.confirmValues] Any of these values confirms.
 * @param {boolean} [options.caseInsensitive] Compare ignoring case (default false).
 * @param {string} [options.inputLabel]
 * @param {string} [options.inputLabelHtml] HTML for the input label (takes precedence).
 * @param {string} [options.inputPlaceholder]
 * @param {string} [options.confirmButtonText]
 * @param {string} [options.cancelButtonText]
 * @param {Object} [options.customClass]
 * @param {function} [options.didOpen]
 *
 * @returns {Promise<boolean>}
 */
export const confirmWithInput = (options) => {
  const normalize = value => {
    const trimmed = `${value || ''}`.trim()

    return options.caseInsensitive ? trimmed.toLowerCase() : trimmed
  }

  const expectedValues = (options.confirmValues?.length
    ? options.confirmValues
    : [options.confirmValue])
    .map(normalize)
    .filter(Boolean)

  // Truthy label so SweetAlert2 creates the node when we inject HTML in didOpen.
  const inputLabel = options.inputLabelHtml
    ? (options.inputLabel || '\u00a0')
    : options.inputLabel

  return swal.fire({
    title: options.title,
    text: options.text,
    html: options.html,
    icon: options.icon ?? 'warning',
    input: 'text',
    inputLabel,
    inputPlaceholder: options.inputPlaceholder,
    showCancelButton: true,
    confirmButtonText: options.confirmButtonText || i18n.t('Confirm'),
    cancelButtonText: options.cancelButtonText || i18n.t('Cancel'),
    customClass: {
      ...options.customClass,
      confirmButton: options.customClass?.confirmButton ?? 'btn btn-primary',
      cancelButton: options.customClass?.cancelButton ?? 'btn btn-outline-danger ml-1',
    },
    buttonsStyling: false,
    didOpen: popup => {
      if (options.inputLabelHtml) {
        const label = popup.querySelector('.swal2-input-label')

        if (label) {
          label.innerHTML = options.inputLabelHtml
        }
      }

      if (typeof options.didOpen === 'function') {
        options.didOpen(popup)
      }
    },
    preConfirm: (value) => {
      const typedValue = normalize(value)

      if (!typedValue || !expectedValues.includes(typedValue)) {
        swal.showValidationMessage(i18n.t('Please type the expected value to confirm.'))
        return false
      }

      return true
    },
  }).then(res => res.isConfirmed)
    .catch(() => {
      errorAlert({ title: options.title })
      return false
    })
}
